import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, TrendingUp, Pencil } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { formatCurrency, formatRelative, cn } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';
import { Dialog } from '@/components/ui/dialog';
import { normalizeOpportunityPayload, sanitizeDecimalInput, sanitizeIntegerInput, type OpportunityFormValues } from '@/lib/form-utils';
import type { ApiResponse, PaginatedResponse, Opportunity, OpportunityStage, Company, Contact } from '@sparqplug/types';

const STAGES: OpportunityStage[] = ['prospecting', 'qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

const STAGE_COLORS: Record<OpportunityStage, string> = {
  lead: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  qualified: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  prospecting: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  qualification: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  proposal: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400',
  negotiation: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  closed_won: 'bg-green-500/15 text-green-600 dark:text-green-400',
  closed_lost: 'bg-muted text-muted-foreground',
};

const STAGE_PROBABILITIES: Record<OpportunityStage, string> = {
  lead: '10',
  qualified: '30',
  prospecting: '10',
  qualification: '30',
  proposal: '50',
  negotiation: '75',
  closed_won: '100',
  closed_lost: '0',
};

const inputClass = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const initialForm: OpportunityFormValues = { name: '', stage: 'prospecting', value: '', probability: STAGE_PROBABILITIES['prospecting'], expectedCloseDate: '', companyId: '', contactId: '' };

function toEditableStage(stage: OpportunityStage): OpportunityFormValues['stage'] {
  if (stage === 'lead') {
    return 'prospecting';
  }

  if (stage === 'qualified') {
    return 'qualification';
  }

  return stage;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }

  return fallback;
}

export function OpportunitiesPage() {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<OpportunityStage | ''>('');
  const [page, setPage] = useState(1);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  const [form, setForm] = useState<OpportunityFormValues>(initialForm);
  const LIMIT = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['opportunities', { stage, page }],
    queryFn: () =>
      api.get<ApiResponse<PaginatedResponse<Opportunity>>>('/opportunities', {
        stage: stage || undefined,
        page,
        limit: LIMIT,
      }),
  });

  const opps = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const createMutation = useMutation({
    mutationFn: (data: OpportunityFormValues) => api.post('/opportunities', normalizeOpportunityPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      toast({ title: 'Opportunity created', variant: 'success' });
      closeDialog();
    },
    onError: (error) =>
      toast({
        title: 'Failed to create opportunity',
        description: getErrorMessage(error, 'Review the opportunity fields and try again.'),
        variant: 'destructive',
      }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: OpportunityFormValues }) =>
      api.patch(`/opportunities/${id}`, normalizeOpportunityPayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      toast({ title: 'Opportunity updated', variant: 'success' });
      closeDialog();
    },
    onError: (error) =>
      toast({
        title: 'Failed to update opportunity',
        description: getErrorMessage(error, 'Review the opportunity fields and try again.'),
        variant: 'destructive',
      }),
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies-list'],
    queryFn: () => api.get<ApiResponse<PaginatedResponse<Company>>>('/companies', { limit: 200 }),
    enabled: showDialog,
  });
  const { data: contactsData } = useQuery({
    queryKey: ['contacts-list'],
    queryFn: () => api.get<ApiResponse<PaginatedResponse<Contact>>>('/contacts', { limit: 200 }),
    enabled: showDialog,
  });
  const companiesList = companiesData?.data?.items ?? [];
  const contactsList = contactsData?.data?.items ?? [];

  function openCreateDialog() {
    setEditingOpportunity(null);
    setForm(initialForm);
    setShowDialog(true);
  }

  function openEditDialog(opportunity: Opportunity) {
    setEditingOpportunity(opportunity);
    const editableStage = toEditableStage(opportunity.stage);
    setForm({
      name: opportunity.name,
      stage: editableStage,
      value: opportunity.value != null ? String(opportunity.value) : '',
      probability: opportunity.probability != null ? String(opportunity.probability) : STAGE_PROBABILITIES[editableStage],
      expectedCloseDate: opportunity.closeDate ? opportunity.closeDate.slice(0, 10) : '',
      companyId: opportunity.companyId ?? '',
      contactId: opportunity.contactId ?? '',
    });
    setShowDialog(true);
  }

  function closeDialog() {
    setShowDialog(false);
    setEditingOpportunity(null);
    setForm(initialForm);
  }

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Opportunities</h1>
          <p className="text-sm text-muted-foreground">{total} total</p>
        </div>
        <button onClick={openCreateDialog} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
          <Plus className="h-4 w-4" />
          New Opportunity
        </button>
      </div>

      {/* Stage filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => { setStage(''); setPage(1); }}
          className={cn('h-7 px-3 rounded-full text-xs font-medium border transition-colors', !stage ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted')}
        >
          All
        </button>
        {STAGES.map((s) => (
          <button
            key={s}
            onClick={() => { setStage(s); setPage(1); }}
            className={cn('h-7 px-3 rounded-full text-xs font-medium border transition-colors capitalize', stage === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted')}
          >
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Opportunity</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Stage</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Value</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground hidden md:table-cell">Probability</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Close Date</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 rounded bg-muted animate-pulse w-3/4" /></td>
                  ))}
                </tr>
              ))
            ) : opps.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No opportunities found</td></tr>
            ) : (
              opps.map((opp) => (
                <tr key={opp.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-foreground">{opp.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize', STAGE_COLORS[opp.stage])}>
                      {opp.stage.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-foreground">
                    {formatCurrency(opp.value ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-muted-foreground">
                    {opp.probability != null ? `${opp.probability}%` : '—'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">
                    {opp.closeDate ? formatRelative(opp.closeDate) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openEditDialog(opp)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={`Edit ${opp.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/10">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-7 px-2.5 rounded text-xs border border-border hover:bg-muted disabled:opacity-40 transition-colors">Prev</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-7 px-2.5 rounded text-xs border border-border hover:bg-muted disabled:opacity-40 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          if (!open && !createMutation.isPending && !updateMutation.isPending) {
            closeDialog();
            return;
          }

          setShowDialog(open);
        }}
        title={editingOpportunity ? 'Edit Opportunity' : 'New Opportunity'}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (createMutation.isPending || updateMutation.isPending) {
              return;
            }

            if (editingOpportunity) {
              updateMutation.mutate({ id: editingOpportunity.id, data: form });
              return;
            }

            createMutation.mutate(form);
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Name *</label>
            <input required autoFocus className={inputClass} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Enterprise deal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Company</label>
              <select aria-label="Opportunity company" className={inputClass} value={form.companyId} onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value }))}>
                <option value="">— None —</option>
                {companiesList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Contact</label>
              <select aria-label="Opportunity contact" className={inputClass} value={form.contactId} onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}>
                <option value="">— None —</option>
                {contactsList.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Stage</label>
              <select aria-label="Opportunity stage" className={inputClass} value={form.stage} onChange={(e) => {
                const s = e.target.value as OpportunityFormValues['stage'];
                setForm((f) => ({ ...f, stage: s, probability: STAGE_PROBABILITIES[s] }));
              }}>
                {STAGES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Value ($)</label>
              <input type="text" inputMode="decimal" className={inputClass} value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: sanitizeDecimalInput(e.target.value) }))} placeholder="0.00" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Probability (%)</label>
              <input type="text" inputMode="numeric" title="Opportunity probability" className={inputClass} value={form.probability} onChange={(e) => setForm((f) => ({ ...f, probability: sanitizeIntegerInput(e.target.value) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Close date</label>
              <input type="date" title="Expected close date" className={inputClass} value={form.expectedCloseDate} onChange={(e) => setForm((f) => ({ ...f, expectedCloseDate: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={closeDialog} className="h-9 px-4 rounded-md border border-border text-sm hover:bg-muted transition-colors">Cancel</button>
            <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors disabled:opacity-50">
              {createMutation.isPending || updateMutation.isPending
                ? editingOpportunity
                  ? 'Saving…'
                  : 'Creating…'
                : editingOpportunity
                  ? 'Save Changes'
                  : 'Create Opportunity'}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
